import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Spin, Result, Button } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { needsTermsConsent } from '../../utils/termsConsent';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requiredPermissions?: string[];
  requireAll?: boolean; // If true, requires ALL permissions/roles; if false (default), requires ANY
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ 
  children, 
  allowedRoles = [], 
  requiredPermissions = [],
  requireAll = false,
}) => {
  const { isAuthenticated, isLoading, hasRole, hasPermission, isSuperAdmin, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Superadmin bypasses all role/permission checks
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Non-superadmin users must accept terms before accessing any protected screen
  if (needsTermsConsent(user)) {
    return <Navigate to="/accept-terms" state={{ from: location }} replace />;
  }

  // Check roles
  let hasRequiredRole = allowedRoles.length === 0; // If no roles specified, pass
  if (allowedRoles.length > 0) {
    if (requireAll) {
      hasRequiredRole = allowedRoles.every(role => hasRole(role));
    } else {
      hasRequiredRole = allowedRoles.some(role => hasRole(role));
    }
  }

  // Check permissions
  let hasRequiredPermission = requiredPermissions.length === 0; // If no permissions specified, pass
  if (requiredPermissions.length > 0) {
    if (requireAll) {
      hasRequiredPermission = requiredPermissions.every(perm => hasPermission(perm));
    } else {
      hasRequiredPermission = requiredPermissions.some(perm => hasPermission(perm));
    }
  }

  // User must pass both role AND permission checks
  if (!hasRequiredRole || !hasRequiredPermission) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Result
          status="403"
          icon={<LockOutlined className="text-6xl text-gray-400" />}
          title="Access Denied"
          subTitle={
            <div className="text-center">
              <p className="text-gray-600 mb-2">
                You don't have permission to access this page.
              </p>
              <p className="text-gray-500 text-sm">
                Your role: <strong>{user?.user_type || 'Unknown'}</strong>
              </p>
              {allowedRoles.length > 0 && (
                <p className="text-gray-500 text-sm">
                  Required roles: <strong>{allowedRoles.join(', ')}</strong>
                </p>
              )}
            </div>
          }
          extra={[
            <Button 
              type="primary" 
              key="dashboard" 
              onClick={() => window.location.href = '/dashboard'}
            >
              Go to Dashboard
            </Button>,
            <Button 
              key="home" 
              onClick={() => window.location.href = '/'}
            >
              Go Home
            </Button>,
          ]}
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default RoleBasedRoute;

